import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Phone, Check, ChevronUp, ChevronDown, Info } from "lucide-react";
import Logo from "../components/site/Logo";
import CalendlyButton from "../components/site/CalendlyButton";
import useSEO from "../lib/useSEO";
import { api, formatApiError } from "../lib/api";
import { GOALS, DURATIONS, MONTHLY_BUDGET_MIN, quoteV2, formatMoney, formatNumber, CPM_RATES } from "../lib/campaign";

const INDUSTRY_OPTIONS = [
  "Automotive", "Beauty", "Casinos & Gaming", "Construction", "Education",
  "Entertainment", "Events & Venues", "Financial Services", "Healthcare",
  "Home Services", "Hospitality", "Legal Services", "Nonprofit Organizations",
  "Political Campaigns", "Professional Services", "Real Estate", "Restaurants",
  "Retail", "Technology", "Tourism", "Wellness", "Other",
];

const SENSITIVE_KEYWORDS = ["politic", "casino", "gambling", "cannabis", "cbd", "mortgage", "payday", "firearm", "alcohol", "tobacco", "pharma", "crypto", "adult"];

const STORAGE_KEY = "rae_planner_v2";
const STEPS = ["Campaign Goal", "Audience & Location", "Budget & Readiness", "Contact & Review"];

const TARGETING_CHOICES = [
  { id: "recommend", title: "Let Rae Recommend", desc: "Describe the audience and let the strategy team build it.", exclusive: true },
  { id: "location_event", title: "Location & Event Targeting", desc: "Reach visitors to competitors, venues, events, neighborhoods, or other physical locations." },
  { id: "addressable", title: "Addressable Targeting", desc: "Reach households from a mailing, customer, donor, or voter-address list." },
  { id: "search_intent", title: "Search & Buyer Intent", desc: "Reach people based on relevant searches, interests, and in-market behavior." },
  { id: "site_retargeting", title: "Website Retargeting", desc: "Re-engage people who visit your website after pixel installation." },
  { id: "demo_layers", title: "Demographic & Behavioral Layers", desc: "Refine an audience using available household and consumer attributes." },
];

const FORMAT_OPTIONS = [
  { id: "static", label: "Static Display" },
  { id: "video", label: "Video" },
  { id: "mobile", label: "Mobile" },
  { id: "ott_ctv", label: "CTV / Streaming TV" },
];

const CONVERSIONS = ["Store / location visit", "Lead", "Sale", "Phone call", "Tour / appointment", "Registration / signup", "Other"];

const initialForm = {
  company: "", industry: "", industry_other: "", primary_goal: "", goal_other: "", launch_date: "", website: "",
  audience_description: "", locations_description: "", specific_targets: "",
  targeting_choices: ["recommend"],
  industry_details: {},
  monthly_budget: "", duration: "", media_mix: "recommended", preferred_formats: [],
  has_ads: "", ad_creation: false, destination_url: "", pixel_status: "", primary_conversion: "",
  name: "", email: "", phone: "", notes: "",
};

const GOAL_PHRASES = {
  "Foot traffic to a location": "drive foot traffic to a location",
  "Online conversions / sales": "drive online conversions and sales",
  "Brand awareness": "build brand awareness",
  "Lead generation": "generate leads",
  "Event attendance": "drive event attendance",
  "App installs": "drive app installs",
};

const isSensitiveIndustry = (v) => {
  const s = String(v || "").toLowerCase();
  return SENSITIVE_KEYWORDS.some((k) => s.includes(k));
};

export default function CampaignBuilderPage() {
  useSEO({
    title: "Start Your Campaign — Guided Campaign Planner",
    description: "Tell us who you want to reach, where they are, and what action you want them to take. Rae Enterprise will recommend the targeting, media mix, and campaign structure.",
    path: "/campaign-builder",
  });
  const [state, setState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && saved.form) return { step: saved.step || 0, form: { ...initialForm, ...saved.form } };
    } catch (e) { /* ignore */ }
    return { step: 0, form: initialForm };
  });
  const { step, form } = state;
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedId, setSubmittedId] = useState(null);
  const [setupWaived, setSetupWaived] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dirtyRef = useRef(false);
  const submittedRef = useRef(false);
  const topRef = useRef(null);

  const set = (patch) => {
    dirtyRef.current = true;
    setState((s) => ({ ...s, form: { ...s.form, ...patch } }));
  };
  const setDetail = (key, value) =>
    set({ industry_details: { ...form.industry_details, [key]: value } });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current && !submittedRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const quote = useMemo(
    () => quoteV2(form.monthly_budget, {
      setupFeeWaived: setupWaived,
      adCreation: form.ad_creation,
      mediaMix: form.media_mix,
      preferredFormats: form.preferred_formats,
    }),
    [form.monthly_budget, setupWaived, form.ad_creation, form.media_mix, form.preferred_formats],
  );

  const checkEmail = async () => {
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) return;
    try {
      const { data } = await api.get(`/campaign-request/check-email?email=${encodeURIComponent(form.email)}`);
      setSetupWaived(!!data.setup_fee_waived);
    } catch (e) { /* non-blocking */ }
  };

  const validateStep = (s) => {
    const errs = {};
    if (s === 0) {
      if (!form.company.trim()) errs.company = "Please enter your business, organization, or campaign name.";
      if (!form.industry) errs.industry = "Please select an industry.";
      if (form.industry === "Other" && !form.industry_other.trim()) errs.industry_other = "Please describe your industry.";
      if (!form.primary_goal) errs.primary_goal = "Please select a primary goal.";
      if (form.primary_goal === "Other" && !form.goal_other.trim()) errs.goal_other = "Please describe your goal.";
      if (!form.website.trim()) errs.website = "Please enter your website.";
    }
    if (s === 2) {
      const b = Number(form.monthly_budget);
      if (!b || b < MONTHLY_BUDGET_MIN) errs.monthly_budget = `Monthly media budget must be at least $${MONTHLY_BUDGET_MIN.toLocaleString()}.`;
      if (form.media_mix === "custom" && form.preferred_formats.length === 0)
        errs.preferred_formats = "Select at least one format, or switch back to the recommended mix.";
    }
    if (s === 3) {
      if (!form.name.trim()) errs.name = "Please enter your name.";
      if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Please enter a valid email address.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setErrors({});
    setState((s) => ({ ...s, step: Math.min(s.step + 1, 3) }));
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const goBack = () => {
    setErrors({});
    setState((s) => ({ ...s, step: Math.max(s.step - 1, 0) }));
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleTargeting = (id) => {
    const cur = new Set(form.targeting_choices);
    if (id === "recommend") {
      set({ targeting_choices: ["recommend"] });
      return;
    }
    cur.delete("recommend");
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    set({ targeting_choices: cur.size ? [...cur] : ["recommend"] });
  };

  const toggleFormat = (id) => {
    const cur = new Set(form.preferred_formats);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    set({ preferred_formats: [...cur] });
  };

  const submit = async () => {
    if (!validateStep(3)) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const titles = form.targeting_choices.map((id) => TARGETING_CHOICES.find((t) => t.id === id)?.title || id);
      const { data } = await api.post("/campaign-request", {
        ...form,
        industry: form.industry === "Other" ? `Other — ${form.industry_other.trim()}` : form.industry,
        primary_goal: form.primary_goal === "Other" ? `Other — ${form.goal_other.trim()}` : form.primary_goal,
        monthly_budget: Number(form.monthly_budget),
        targeting_choices: titles,
        phone: form.phone || null,
      });
      submittedRef.current = true;
      localStorage.removeItem(STORAGE_KEY);
      setSubmittedId(data.id);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again — your answers are saved.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <PlannerShell>
        <div className="max-w-[640px] mx-auto px-6 pt-20 pb-28 text-center" data-testid="planner-success">
          <div className="w-14 h-14 mx-auto border border-[#d4af37] flex items-center justify-center mb-8">
            <Check size={24} strokeWidth={1.5} className="text-[#d4af37]" />
          </div>
          <h1 className="h-display text-white text-[34px] sm:text-[44px] mb-6">
            YOUR CAMPAIGN REQUEST HAS<br />BEEN <span className="text-[#d4af37]">RECEIVED.</span>
          </h1>
          <p className="text-white/70 text-[15px] leading-relaxed mb-4">
            A Rae Enterprise strategist will review the audience, targeting, compliance
            requirements, and exact pricing before contacting you.
          </p>
          <p className="text-white/50 text-[13px] leading-relaxed mb-10">
            Campaigns generally require 5–7 business days to launch after payment and
            all necessary assets are received.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <CalendlyButton testId="planner-success-calendly">Book a Strategy Call</CalendlyButton>
            <Link to="/" className="btn-outline-gold btn-label" data-testid="planner-success-home">Return Home</Link>
          </div>
        </div>
      </PlannerShell>
    );
  }

  return (
    <PlannerShell>
      <div ref={topRef} />
      {/* Compact intro */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-8">
        <div className="kicker mb-3" data-testid="planner-eyebrow">Guided Campaign Planner</div>
        <h1 className="h-display text-white text-[34px] sm:text-[44px] leading-[0.95] mb-4" data-testid="planner-headline">
          START YOUR <span className="text-[#d4af37]">CAMPAIGN.</span>
        </h1>
        <p className="text-white/75 text-[14px] max-w-[620px] mb-4">
          Tell us who you want to reach, where they are, and what action you want them
          to take. Rae Enterprise will recommend the targeting, media mix, and campaign structure.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[11px] tracking-[0.15em] uppercase text-[#d4af37]" data-testid="planner-reassurance">
          <span>Takes about 2 minutes</span><span className="text-white/30">·</span>
          <span>No payment required</span><span className="text-white/30">·</span>
          <span>Reviewed by a Rae Enterprise strategist</span>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 mb-8">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] tracking-[0.2em] uppercase text-white/65" data-testid="planner-progress-label">
            Step {step + 1} of 4 — <span className="text-[#d4af37]">{STEPS[step]}</span>
          </span>
        </div>
        <div className="h-[3px] bg-white/10">
          <div className="h-full bg-[#d4af37] transition-all duration-500" style={{ width: `${((step + 1) / 4) * 100}%` }} />
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-32 lg:pb-20 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 items-start">
        <div className="space-y-8">
          {step === 0 && (
            <section className="space-y-6" data-testid="planner-step-0">
              <Field label="Business, organization, or campaign name" required error={errors.company}>
                <input className={inputCls(errors.company)} value={form.company} onChange={(e) => set({ company: e.target.value })} data-testid="field-company" />
              </Field>
              <Field label="Industry" required error={errors.industry}>
                <select className={inputCls(errors.industry)} value={form.industry} onChange={(e) => set({ industry: e.target.value })} data-testid="field-industry">
                  <option value="">Select your industry…</option>
                  {INDUSTRY_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                {form.industry === "Other" && (
                  <div className="mt-3">
                    <label className="block text-[12px] tracking-[0.15em] uppercase text-white/75 mb-2">Please describe your industry <span className="text-[#d4af37]">*</span></label>
                    <input className={inputCls(errors.industry_other)} value={form.industry_other} onChange={(e) => set({ industry_other: e.target.value })} data-testid="field-industry-other" />
                    {errors.industry_other && <p className="mt-2 text-red-400 text-[13px]" data-testid="error-industry-other">{errors.industry_other}</p>}
                  </div>
                )}
                {isSensitiveIndustry(form.industry) && (
                  <p className="mt-3 text-[13px] text-white/70 flex items-start gap-2" data-testid="industry-compliance-note">
                    <Info size={14} className="text-[#d4af37] mt-0.5 shrink-0" />
                    This industry may require additional platform and compliance review. Rae Enterprise will confirm eligible targeting before launch.
                  </p>
                )}
              </Field>
              <Field label="Primary goal" required error={errors.primary_goal}>
                <select className={inputCls(errors.primary_goal)} value={form.primary_goal} onChange={(e) => set({ primary_goal: e.target.value })} data-testid="field-goal">
                  <option value="">Select a goal…</option>
                  {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                  <option value="Other">Other</option>
                </select>
                {form.primary_goal === "Other" && (
                  <div className="mt-3">
                    <label className="block text-[12px] tracking-[0.15em] uppercase text-white/75 mb-2">Please describe your goal <span className="text-[#d4af37]">*</span></label>
                    <input className={inputCls(errors.goal_other)} value={form.goal_other} onChange={(e) => set({ goal_other: e.target.value })} data-testid="field-goal-other" />
                    {errors.goal_other && <p className="mt-2 text-red-400 text-[13px]" data-testid="error-goal-other">{errors.goal_other}</p>}
                  </div>
                )}
              </Field>
              <Field label="Desired launch date or event dates" hint="Optional">
                <input className={inputCls()} placeholder="e.g., Early September, or Oct 12–14 event" value={form.launch_date} onChange={(e) => set({ launch_date: e.target.value })} data-testid="field-launch-date" />
              </Field>
              <Field label="Website" required error={errors.website}>
                <input className={inputCls(errors.website)} placeholder="https://" value={form.website} onChange={(e) => set({ website: e.target.value })} data-testid="field-website" />
              </Field>
            </section>
          )}

          {step === 1 && (
            <section className="space-y-6" data-testid="planner-step-1">
              <Field label="Who do you want to reach?" hint="Plain language is perfect — e.g., 'people shopping for a new SUV' or 'homeowners near our showroom'">
                <textarea rows={3} className={inputCls()} value={form.audience_description} onChange={(e) => set({ audience_description: e.target.value })} data-testid="field-audience" />
              </Field>
              <Field label="Where do you want to reach them?" hint="Cities, neighborhoods, regions — anywhere in the U.S.">
                <textarea rows={2} className={inputCls()} value={form.locations_description} onChange={(e) => set({ locations_description: e.target.value })} data-testid="field-locations" />
              </Field>
              <Field label="Specific competitors, venues, events, ZIP codes, political districts, or addresses?" hint="Optional — one per line is easiest">
                <textarea rows={3} className={inputCls()} value={form.specific_targets} onChange={(e) => set({ specific_targets: e.target.value })} data-testid="field-specific-targets" />
              </Field>

              <div>
                <div className="text-[12px] tracking-[0.2em] uppercase text-white/75 mb-4">How should we build your audience?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TARGETING_CHOICES.map((t) => {
                    const active = form.targeting_choices.includes(t.id);
                    return (
                      <button
                        key={t.id} type="button" onClick={() => toggleTargeting(t.id)}
                        className={`text-left p-5 border transition-colors duration-200 ${active ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/15 hover:border-[#d4af37]/50"}`}
                        data-testid={`targeting-${t.id}`}
                      >
                        <div className={`text-[13px] tracking-[0.12em] uppercase mb-2 ${active ? "text-[#d4af37]" : "text-white"}`}>{t.title}</div>
                        <div className="text-white/70 text-[12px] leading-relaxed">{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.industry === "Political Campaigns" && (
                <ConditionalBlock title="Political campaign details" testId="political-details">
                  <Field label="District / jurisdiction"><input className={inputCls()} value={form.industry_details.district || ""} onChange={(e) => setDetail("district", e.target.value)} data-testid="field-district" /></Field>
                  <Field label="Election date"><input className={inputCls()} value={form.industry_details.election_date || ""} onChange={(e) => setDetail("election_date", e.target.value)} data-testid="field-election-date" /></Field>
                  <Field label="Do you have a voter or address list?">
                    <select className={inputCls()} value={form.industry_details.voter_list || ""} onChange={(e) => setDetail("voter_list", e.target.value)} data-testid="field-voter-list">
                      <option value="">Select…</option><option>Yes</option><option>No</option><option>Not sure</option>
                    </select>
                  </Field>
                  <Field label="Campaign objective"><input className={inputCls()} placeholder="e.g., persuasion, GOTV, name recognition" value={form.industry_details.political_objective || ""} onChange={(e) => setDetail("political_objective", e.target.value)} data-testid="field-political-objective" /></Field>
                  <Field label={'Exact "Paid for by" language'}><input className={inputCls()} value={form.industry_details.paid_for_by || ""} onChange={(e) => setDetail("paid_for_by", e.target.value)} data-testid="field-paid-for-by" /></Field>
                </ConditionalBlock>
              )}
              {(form.industry === "Events & Venues" || form.primary_goal === "Event attendance") && (
                <ConditionalBlock title="Event details" testId="event-details">
                  <Field label="Venue name & address"><input className={inputCls()} value={form.industry_details.venue || ""} onChange={(e) => setDetail("venue", e.target.value)} data-testid="field-venue" /></Field>
                  <Field label="Event dates"><input className={inputCls()} value={form.industry_details.event_dates || ""} onChange={(e) => setDetail("event_dates", e.target.value)} data-testid="field-event-dates" /></Field>
                  <Field label="Estimated attendance"><input className={inputCls()} value={form.industry_details.attendance || ""} onChange={(e) => setDetail("attendance", e.target.value)} data-testid="field-attendance" /></Field>
                  <Field label="Post-event retargeting period" hint="Optional — e.g., 30 days"><input className={inputCls()} value={form.industry_details.retargeting_period || ""} onChange={(e) => setDetail("retargeting_period", e.target.value)} data-testid="field-retargeting-period" /></Field>
                </ConditionalBlock>
              )}
              {(form.industry === "Retail" || form.industry === "Automotive") && (
                <ConditionalBlock title={`${form.industry} details`} testId="retail-details">
                  <Field label="Competitor locations" hint="One per line"><textarea rows={2} className={inputCls()} value={form.industry_details.competitor_locations || ""} onChange={(e) => setDetail("competitor_locations", e.target.value)} data-testid="field-competitors" /></Field>
                  <Field label="Your location address (conversion zone)"><input className={inputCls()} value={form.industry_details.conversion_zone_address || ""} onChange={(e) => setDetail("conversion_zone_address", e.target.value)} data-testid="field-conversion-zone" /></Field>
                </ConditionalBlock>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="space-y-6" data-testid="planner-step-2">
              <Field label="Monthly media budget" required error={errors.monthly_budget} hint={`Minimum $${MONTHLY_BUDGET_MIN.toLocaleString()}/month`}>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d4af37]">$</span>
                  <input type="number" min={MONTHLY_BUDGET_MIN} className={`${inputCls(errors.monthly_budget)} pl-8`} value={form.monthly_budget} onChange={(e) => set({ monthly_budget: e.target.value })} data-testid="field-budget" />
                </div>
                <ImpressionsCalculator onUseBudget={(b) => set({ monthly_budget: String(b) })} />
              </Field>
              <Field label="Campaign duration">
                <select className={inputCls()} value={form.duration} onChange={(e) => set({ duration: e.target.value })} data-testid="field-duration">
                  <option value="">Select…</option>
                  {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>

              <div>
                <div className="text-[12px] tracking-[0.2em] uppercase text-white/75 mb-4">Media mix</div>
                <button type="button" onClick={() => set({ media_mix: "recommended", preferred_formats: [] })}
                  className={`w-full text-left p-5 border mb-3 transition-colors ${form.media_mix === "recommended" ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/15 hover:border-[#d4af37]/50"}`}
                  data-testid="mix-recommended">
                  <div className={`text-[13px] tracking-[0.12em] uppercase mb-2 ${form.media_mix === "recommended" ? "text-[#d4af37]" : "text-white"}`}>Recommended media mix</div>
                  <div className="text-white/70 text-[12px]">Rae Enterprise will allocate your budget based on your audience, objective, and available inventory.</div>
                </button>
                <button type="button" onClick={() => set({ media_mix: "custom" })}
                  className={`w-full text-left p-5 border transition-colors ${form.media_mix === "custom" ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/15 hover:border-[#d4af37]/50"}`}
                  data-testid="mix-custom">
                  <div className={`text-[13px] tracking-[0.12em] uppercase ${form.media_mix === "custom" ? "text-[#d4af37]" : "text-white"}`}>I want to choose my formats</div>
                </button>
                {form.media_mix === "custom" && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {FORMAT_OPTIONS.map((f) => (
                        <button key={f.id} type="button" onClick={() => toggleFormat(f.id)}
                          className={`text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${form.preferred_formats.includes(f.id) ? "border-[#d4af37] bg-[#d4af37] text-black" : "border-white/20 text-white/70 hover:border-[#d4af37]/60"}`}
                          data-testid={`format-${f.id}`}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                    {errors.preferred_formats && <p className="mt-2 text-red-400 text-[13px]" data-testid="formats-error">{errors.preferred_formats}</p>}
                  </div>
                )}
              </div>

              <Field label="Do you already have ads?">
                <div className="flex flex-wrap gap-2">
                  {["Yes, ready to run", "No — we'll need creative"].map((v) => (
                    <button key={v} type="button" onClick={() => set({ has_ads: v, ad_creation: v.startsWith("No") ? form.ad_creation : false })}
                      className={`text-[12px] tracking-wide px-4 py-2.5 border transition-colors ${form.has_ads === v ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-white/20 text-white/70 hover:border-[#d4af37]/60"}`}
                      data-testid={`has-ads-${v.startsWith("Yes") ? "yes" : "no"}`}>
                      {v}
                    </button>
                  ))}
                </div>
                {form.has_ads.startsWith("No") && (
                  <label className="mt-4 flex items-start gap-3 cursor-pointer p-4 border border-[#d4af37]/30 bg-[#d4af37]/5" data-testid="ad-creation-toggle">
                    <input type="checkbox" checked={form.ad_creation} onChange={(e) => set({ ad_creation: e.target.checked })} className="mt-1 accent-[#d4af37]" data-testid="ad-creation-checkbox" />
                    <span>
                      <span className="block text-white text-[13px] mb-1">Add professional ad creation — $300 one-time</span>
                      <span className="block text-white/70 text-[12px]">Our team designs on-brand creative in every size and format your campaign needs.</span>
                    </span>
                  </label>
                )}
              </Field>

              <Field label="Destination URL" hint="Where should clicks go?">
                <input className={inputCls()} placeholder="https://" value={form.destination_url} onChange={(e) => set({ destination_url: e.target.value })} data-testid="field-destination" />
              </Field>
              <Field label="Primary conversion">
                <select className={inputCls()} value={form.primary_conversion} onChange={(e) => set({ primary_conversion: e.target.value })} data-testid="field-conversion">
                  <option value="">Select…</option>
                  {CONVERSIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </section>
          )}

          {step === 3 && (
            <section className="space-y-6" data-testid="planner-step-3">
              <Field label="Name" required error={errors.name}>
                <input className={inputCls(errors.name)} value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="field-name" />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input type="email" className={inputCls(errors.email)} value={form.email} onChange={(e) => set({ email: e.target.value })} onBlur={checkEmail} data-testid="field-email" />
                {setupWaived && <p className="mt-2 text-[#d4af37] text-[12px]" data-testid="setup-waived-note">Welcome back — your one-time setup fee is waived.</p>}
              </Field>
              <Field label="Phone" hint="Optional">
                <input className={inputCls()} value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="field-phone" />
              </Field>
              <Field label="Anything else we should know?" hint="Optional">
                <textarea rows={3} className={inputCls()} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="field-notes" />
              </Field>

              <div className="border border-white/10 p-6" data-testid="planner-summary">
                <div className="text-[12px] tracking-[0.2em] uppercase text-[#d4af37] mb-4">Your plan at a glance</div>
                <p className="text-white/70 text-[14px] leading-relaxed">
                  {form.company || "Your business"}{form.industry ? ` (${form.industry})` : ""} wants to {GOAL_PHRASES[form.primary_goal] || "grow"}
                  {form.audience_description ? `, reaching ${form.audience_description.trim()}` : ""}
                  {form.locations_description ? ` in ${form.locations_description.trim()}` : ""}.
                  {" "}Budget of {formatMoney(form.monthly_budget)}/month{form.duration ? ` for ${form.duration.toLowerCase()}` : ""},
                  {" "}{form.media_mix === "custom" && form.preferred_formats.length ? `using ${form.preferred_formats.join(", ")}` : "with a media mix recommended by Rae Enterprise"}.
                  {form.ad_creation ? " Includes professional ad creation." : ""}
                  {" "}A strategist will confirm targeting, compliance, and exact pricing before anything launches.
                </p>
              </div>
              {submitError && <p className="text-red-400 text-[13px]" data-testid="submit-error">{submitError}</p>}
            </section>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between pt-4">
            {step > 0 ? (
              <button type="button" onClick={goBack} className="inline-flex items-center gap-2 text-white/60 hover:text-[#d4af37] text-[12px] tracking-[0.18em] uppercase transition" data-testid="planner-back-btn">
                <ArrowLeft size={14} /> Back
              </button>
            ) : <span />}
            {step < 3 ? (
              <button type="button" onClick={goNext} className="btn-gold btn-label" data-testid="planner-next-btn">
                Next <ArrowRight size={15} strokeWidth={1.6} />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} className="btn-gold btn-label disabled:opacity-60" data-testid="request-plan-btn">
                {submitting ? "Submitting…" : "Request My Campaign Plan"} <ArrowRight size={15} strokeWidth={1.6} />
              </button>
            )}
          </div>
        </div>

        {/* Desktop estimate sidebar */}
        <aside className="hidden lg:block sticky top-24" data-testid="estimate-sidebar">
          <EstimatePanel quote={quote} />
        </aside>
      </div>

      {/* Mobile estimate drawer */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#d4af37]/30 bg-[#0b0b0b]" data-testid="estimate-drawer">
        <button type="button" onClick={() => setDrawerOpen((v) => !v)} className="w-full flex items-center justify-between px-6 py-4" data-testid="estimate-drawer-toggle">
          <span className="text-[12px] tracking-[0.2em] uppercase text-[#d4af37]">View estimate</span>
          <span className="flex items-center gap-3">
            <span className="text-white text-[15px] font-medium">{formatMoney(quote.first_month_total)} first month</span>
            {drawerOpen ? <ChevronDown size={16} className="text-[#d4af37]" /> : <ChevronUp size={16} className="text-[#d4af37]" />}
          </span>
        </button>
        {drawerOpen && <div className="px-6 pb-6"><EstimatePanel quote={quote} compact /></div>}
      </div>
    </PlannerShell>
  );
}

function ImpressionsCalculator({ onUseBudget }) {
  const [open, setOpen] = useState(false);
  const [impr, setImpr] = useState("");
  const budget = Math.max(Math.ceil(((Number(impr) || 0) / 1000) * CPM_RATES.static), 0);
  const suggested = Math.max(budget, MONTHLY_BUDGET_MIN);
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[#d4af37] hover:text-[#f2c66d] text-[13px] tracking-wide transition" data-testid="impressions-calc-toggle">
        {open ? "Hide impressions calculator" : "Not sure what to budget? Try the impressions calculator →"}
      </button>
      {open && (
        <div className="mt-3 border border-[#d4af37]/25 bg-[#0d0d0d] p-5" data-testid="impressions-calc">
          <label className="block text-[12px] tracking-[0.15em] uppercase text-white/75 mb-2">How many monthly impressions would you like?</label>
          <input type="number" min={0} className={inputCls()} placeholder="e.g., 100000" value={impr} onChange={(e) => setImpr(e.target.value)} data-testid="calc-impressions-input" />
          {Number(impr) > 0 && (
            <div className="mt-4">
              <p className="text-white/80 text-[14px]" data-testid="calc-budget-result">
                ≈ <span className="text-[#d4af37] font-medium">{formatMoney(suggested)}/month</span> for {formatNumber(Number(impr))} impressions at our standard $16 CPM
                {budget < MONTHLY_BUDGET_MIN ? ` (our $${MONTHLY_BUDGET_MIN.toLocaleString()} monthly minimum applies)` : ""}
              </p>
              <button type="button" onClick={() => { onUseBudget(suggested); setOpen(false); }} className="mt-3 text-[12px] tracking-[0.15em] uppercase border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black px-4 py-2 transition-colors" data-testid="calc-use-budget-btn">
                Use this budget
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlannerShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white">
      <header className="sticky top-0 z-50 bg-[#0b0b0b]/95 backdrop-blur border-b border-white/5" data-testid="planner-header">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-[64px] flex items-center justify-between">
          <Link to="/" data-testid="planner-logo-link"><Logo /></Link>
          <div className="flex items-center gap-6">
            <a href="tel:2533318487" className="flex items-center gap-2 text-[#d4af37] text-[12px] tracking-widest" data-testid="planner-phone">
              <Phone size={13} strokeWidth={1.4} /> 253-331-8487
            </a>
            <Link to="/contact" className="hidden sm:block text-white/60 hover:text-[#d4af37] text-[12px] tracking-[0.15em] uppercase transition" data-testid="planner-help-link">Help</Link>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function EstimatePanel({ quote, compact = false }) {
  return (
    <div className={compact ? "" : "border border-[#d4af37]/25 bg-[#0d0d0d] p-8"}>
      {!compact && <div className="kicker mb-6">Your Estimate</div>}
      <ul className="space-y-3">
        <EstRow label="Monthly media" value={formatMoney(quote.media)} testId="est-monthly-media" />
        <EstRow label="Estimated monthly impressions" value={formatNumber(quote.impressions)} testId="est-impressions" />
        <EstRow label="One-time setup" value={quote.setup_fee_waived ? "WAIVED" : formatMoney(quote.setup_fee)} testId="est-setup" gold={quote.setup_fee_waived} />
        {quote.ad_creation_fee > 0 && <EstRow label="One-time ad creation" value={formatMoney(quote.ad_creation_fee)} testId="est-ad-creation" />}
        <EstRow label="First-month total" value={formatMoney(quote.first_month_total)} testId="est-first-month" big />
        <EstRow label="Estimated three-month total" value={formatMoney(quote.three_month_total)} testId="est-three-month" />
      </ul>
      <p className="mt-6 text-white/55 text-[12px] leading-relaxed" data-testid="est-disclaimer">
        Estimates are subject to audience size, inventory availability, platform approval, and final targeting configuration.
      </p>
    </div>
  );
}

function EstRow({ label, value, big = false, gold = false, testId }) {
  return (
    <li className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3">
      <span className="text-[11px] tracking-[0.15em] uppercase text-white/65">{label}</span>
      <span className={`${big ? "text-[20px] text-[#d4af37] font-medium" : gold ? "text-[14px] text-[#d4af37]" : "text-[14px] text-white"}`} data-testid={testId}>{value}</span>
    </li>
  );
}

function ConditionalBlock({ title, children, testId }) {
  return (
    <div className="border border-[#d4af37]/20 bg-[#0d0d0d] p-6 space-y-5" data-testid={testId}>
      <div className="text-[12px] tracking-[0.2em] uppercase text-[#d4af37]">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, hint, required, error, children }) {
  return (
    <div>
      <label className="block text-[12px] tracking-[0.2em] uppercase text-white/75 mb-2">
        {label} {required && <span className="text-[#d4af37]">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-2 text-white/55 text-[13px]">{hint}</p>}
      {error && <p className="mt-2 text-red-400 text-[13px]" data-testid={`error-${label.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>{error}</p>}
    </div>
  );
}

const inputCls = (err) =>
  `w-full bg-[#0d0d0d] border ${err ? "border-red-500/70" : "border-white/15"} focus:border-[#d4af37] outline-none px-4 py-3 text-white text-[14px] transition-colors`;
